/**
 * sl-kqzl.5: Auto-trigger for the permission-overview coachmark.
 *
 * Fires once — the first time the panel renders with a non-empty
 * `workflowId` on the config — then persists `completed` via
 * TutorialPersistence so it never fires again for this user on this
 * device.
 */
import { useService } from '@toeverything/infra';
import { useEffect, useRef } from 'react';

import { TutorialPersistence } from '../../services/tutorial-persistence';
import { TutorialRegistry } from '../../services/tutorial-registry';
import { PERMISSION_OVERVIEW_SESSION_ID } from './permission-overview';

/**
 * Fires ONLY on the null → non-null transition of `workflowId` (user just
 * finished first setup). Panels that open with a pre-existing workflowId
 * do not trigger — they weren't a fresh setup. Once the tutorial is marked
 * completed, future transitions are ignored too. Deferred sessions can be
 * shown again on a later fresh setup.
 */
export function usePermissionOverviewTrigger(
  workflowId: string | null | undefined
): void {
  const registry = useService(TutorialRegistry);
  const persistence = useService(TutorialPersistence);
  const prevWorkflowIdRef = useRef<string | null | undefined>(workflowId);

  useEffect(() => {
    const prev = prevWorkflowIdRef.current;
    prevWorkflowIdRef.current = workflowId;

    const isFreshSetup = !prev && !!workflowId;
    if (!isFreshSetup) return;
    if (persistence.isCompleted(PERMISSION_OVERVIEW_SESSION_ID)) {
      return;
    }
    // Small delay so the panel has painted the spotlight targets.
    const timer = setTimeout(() => {
      registry.start(PERMISSION_OVERVIEW_SESSION_ID);
    }, 600);
    return () => clearTimeout(timer);
  }, [workflowId, registry, persistence]);
}
